import React from "react";
import { EvaluationCriteria } from "../../../model_gen/evaluation";
import { Checkbox } from "../../ui/checkbox";
import { PlusIcon, InfoIcon, PencilIcon } from "lucide-react";
import { Button } from "../../ui/button";
import { Tooltip } from "antd";
import { renderCriteriaTooltip } from "./CriteriaTooltip";

interface CustomCriteriaSectionProps {
  customCriteria: EvaluationCriteria[];
  selectedCriteriaIds: number[];
  toggleCriteria: (id: number) => void;
  onEditCriteria: (crit: EvaluationCriteria) => void;
  onAddCriteria: () => void;
}

function scoringLabel(crit: EvaluationCriteria): string | null {
  const cfg = crit.config as Record<string, any> | undefined;
  const st = cfg?.scoring_type;
  if (st === "boolean") return "Pass / Fail";
  if (st === "tags") return `Tags (${cfg?.tags?.length ?? 0})`;
  if (st === "numeric") return `${cfg?.min ?? 1}–${cfg?.max ?? 5}`;
  return null;
}

const CustomCriteriaSection: React.FC<CustomCriteriaSectionProps> = ({
  customCriteria,
  selectedCriteriaIds,
  toggleCriteria,
  onEditCriteria,
  onAddCriteria,
}) => (
  <div className="space-y-3">
    {customCriteria.map((crit) => {
      const isSelected = selectedCriteriaIds.includes(crit.id!);
      const badge = scoringLabel(crit);
      return (
        <div
          key={crit.id}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors"
        >
          <Checkbox
            id={`custom-${crit.id}`}
            checked={isSelected}
            onCheckedChange={() => toggleCriteria(crit.id!)}
          />
          <label
            htmlFor={`custom-${crit.id}`}
            className="text-sm font-gc-med text-foreground cursor-pointer"
          >
            {crit.name}
          </label>
          {badge && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded font-gc-med uppercase">
              {badge}
            </span>
          )}
          <Tooltip
            placement="right"
            title={renderCriteriaTooltip(crit)}
            overlayStyle={{ maxWidth: 680 }}
          >
            <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
          </Tooltip>
          <div className="flex-1" />
          <button
            onClick={() => onEditCriteria(crit)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      );
    })}

    <Button
      variant="outline"
      size="sm"
      onClick={onAddCriteria}
      className="gap-2"
    >
      <PlusIcon className="w-4 h-4" />
      Add Custom Criterion
    </Button>
  </div>
);

export default CustomCriteriaSection;
